// ============================================================
// src/pages/privacypolicy.js
// Privacy Policy for Performer Pocket Planner.
// ============================================================

import { useState }  from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase }  from '../lib/supabaseClient'

const EFFECTIVE_DATE = 'May 1, 2026'
const CONTACT_SITE   = 'https://ben-perk.com'
const CONTACT_LABEL  = 'ben-perk.com'
const APP_URL        = 'https://drag-helper.netlify.app/'
const APP_NAME       = 'Performer Pocket Planner'

// Reusable section wrapper
function PolicySection({ title, children }) {
  return (
    <section style={{ marginBottom: 'var(--spacing-xl)' }}>
      <h2 style={{
        fontFamily: 'var(--font-display)',
        color: 'var(--primary)',
        fontSize: 'var(--text-xl)',
        marginBottom: 'var(--spacing-md)',
        paddingBottom: '0.5rem',
        borderBottom: '2px solid var(--accent-color)',
      }}>
        {title}
      </h2>
      {children}
    </section>
  )
}

const prose = {
  color: 'var(--neutral-1)',
  lineHeight: 1.8,
  marginBottom: 'var(--spacing-md)',
  fontSize: 'var(--text-base)',
}

const listStyle = {
  color: 'var(--neutral-1)',
  lineHeight: 2,
  paddingLeft: '1.5rem',
  marginBottom: 'var(--spacing-md)',
}


function PrivacyPolicy() {
  const navigate = useNavigate()

  // DELETE ACCOUNT FLOW
  // Three steps:
  //   1. User clicks "Delete My Account" - confirmation card expands
  //   2. User reads what gets deleted
  //   3. User types "DELETE" exactly - only then does the button unlock
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteInput,       setDeleteInput]       = useState('')
  const [deleting,          setDeleting]          = useState(false)
  const [deleteError,       setDeleteError]       = useState(null)

  const canDelete = deleteInput === 'DELETE'

  async function handleDeleteAccount() {
    if (!canDelete || deleting) return
    setDeleting(true)
    setDeleteError(null)

    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('Session expired. Please sign in again.')

      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/delete-account`,
        {
          method:  'POST',
          headers: {
            'Content-Type':  'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
        }
      )

      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) {
        throw new Error(data.error || `Server returned ${res.status}`)
      }

      await supabase.auth.signOut()
      navigate('/login?deleted=1')

    } catch (err) {
      console.error('[PrivacyPolicy] Delete account error:', err)
      setDeleteError('Could not delete account: ' + err.message + '. Please try again or contact support at ben-perk.com.')
      setDeleting(false)
    }
  }

  return (
    <div style={{ maxWidth: '720px', margin: '0 auto', padding: 'var(--spacing-xl) var(--spacing-md)' }}>

      {/* Back navigation */}
      <Link to="/about" style={{ color: 'var(--primary-accessible)', fontWeight: 'var(--weight-bold)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.3rem', marginBottom: 'var(--spacing-lg)' }}>
        <i className="bi bi-arrow-left"></i> Back to About
      </Link>

      {/* Page header */}
      <div style={{ marginBottom: 'var(--spacing-xl)' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', color: 'var(--primary)', fontSize: 'var(--text-4xl)', marginBottom: 'var(--spacing-sm)' }}>
          Privacy Policy
        </h1>
        <p style={{ color: 'var(--neutral-1)', opacity: 0.6, fontSize: 'var(--text-sm)', margin: 0 }}>
          <strong>Effective date:</strong> {EFFECTIVE_DATE} &nbsp;&middot;&nbsp;
          <strong>App:</strong> {APP_NAME} &nbsp;&middot;&nbsp;
          <strong>URL:</strong> <a href={APP_URL} style={{ color: 'var(--primary-accessible)' }}>{APP_URL}</a>
        </p>
      </div>


      {/* 1. INTRODUCTION */}
      <PolicySection title="1. Introduction">
        <p style={prose}>
          This is the privacy policy for <strong>{APP_NAME}</strong>. It explains what data we
          collect, why we collect it, and what your rights are.
        </p>
        <p style={prose}>
          The short version: we collect only what the app needs to work. We do not sell your data.
          We do not run ads. This is a small independent project built by a performer, for performers.
        </p>
        <p style={prose}>
          By creating an account you agree to the practices described here. If you do not agree,
          please do not use the app.
        </p>
        <p style={prose}>
          This app is in <strong>beta</strong>. We include California privacy rights (CCPA/CPRA)
          because the law requires it, but the spirit of this policy is simple: your data is yours
          and we only use it to run the app.
        </p>
      </PolicySection>


      {/* 2. INFORMATION WE COLLECT */}
      <PolicySection title="2. What we collect and why">

        <p style={{ ...prose, marginBottom: 'var(--spacing-sm)' }}>
          <strong>What you give us directly:</strong>
        </p>
        <ul style={listStyle}>
          <li><strong>Email address</strong> - to create your account and log you in.</li>
          <li><strong>Performer name</strong> - your stage name, shown on your dashboard and used in exported filenames.</li>
          <li><strong>Gig details</strong> - venue names, addresses, dates, show times, call times, payment amounts, tips, and setlists.</li>
          <li><strong>Contacts</strong> - names and social handles of bookers or collaborators you choose to save.</li>
          <li><strong>Music files</strong> - audio tracks you upload for the setlist planner. Only you can see them.</li>
          <li><strong>Photos</strong> - promo images you upload. Only you can see them. Please keep photos appropriate.</li>
          <li>
            <strong>Home address or GPS location</strong> - if you turn on gig reminders, you can
            enter a home address or grant browser GPS permission. We convert this to a latitude and
            longitude (using OpenStreetMap / Nominatim) and store those coordinates so we can
            calculate your drive time to venues. Location is optional.
          </li>
          <li>
            <strong>Trip and travel data</strong> - if you use the Trip Planner, we store your
            trip type, destination, stay address, transport mode, travel dates, timezone, and
            terminal details. This is used only to route gig timings from your stay location
            while you are away from home.
          </li>
          <li>
            <strong>Push notification subscription</strong> - if you turn on push notifications,
            OneSignal registers your browser or installed app and gives it a device ID. We store
            that ID so our server can send you gig reminders. You can revoke this at any time in Settings.
          </li>
          <li>
            <strong>Notification preferences</strong> - your prep time (how many minutes you need
            to get ready) and advance warning time (how far ahead you want a heads-up).
          </li>
          <li>
            <strong>Social media handles</strong> - Instagram, X, TikTok, Bluesky, YouTube, and
            Patreon handles you optionally add for use in your promo poster footer.
          </li>
          <li>
            <strong>Travel checklist items</strong> - packing list entries. Only you can see them.
          </li>
        </ul>

        <p style={{ ...prose, marginBottom: 'var(--spacing-sm)' }}>
          <strong>What is collected automatically:</strong>
        </p>
        <ul style={listStyle}>
          <li><strong>Auth tokens</strong> - generated by Supabase to keep you logged in. Stored in your browser only.</li>
          <li><strong>Terminal cache</strong> - airport and station names you have searched are saved in your browser's local storage to speed up future searches. This never leaves your device.</li>
          <li><strong>Basic server logs</strong> - Supabase may log connection metadata (timestamps, IP addresses) as part of standard database operation. We do not use this for tracking or profiling.</li>
          <li><strong>OneSignal device info</strong> - OneSignal may collect device type, OS version, and delivery status for notifications you opt into. See their privacy policy for details.</li>
        </ul>

        <p style={{ ...prose, marginBottom: 'var(--spacing-sm)' }}>
          <strong>What we do NOT collect:</strong>
        </p>
        <ul style={listStyle}>
          <li>No advertising or analytics trackers of any kind.</li>
          <li>No payment card details. The app is currently free.</li>
          <li>No cookies beyond what Supabase needs for authentication.</li>
          <li>We do not use your photos, music, gigs, or personal data for anything other than running the app.</li>
          <li>We do not sell, rent, share, or trade your personal information to any third party for their own purposes.</li>
        </ul>
      </PolicySection>


      {/* 3. HOW WE USE YOUR INFORMATION */}
      <PolicySection title="3. How we use your information">
        <p style={{ ...prose, marginBottom: 'var(--spacing-sm)' }}>
          Everything we collect is used solely to make the app work:
        </p>
        <ul style={listStyle}>
          <li>To log you in and keep your account secure.</li>
          <li>To store and display your gigs, contacts, music, and photos.</li>
          <li>To calculate and show your earnings dashboard.</li>
          <li>To generate calendar export files (.ics) from your gig data.</li>
          <li>To display your performer name and social handles on the dashboard and promo posters.</li>
          <li>
            To calculate drive time and leave-by times. Your stored home coordinates (or stay
            coordinates when you are on a trip) and the venue coordinates are sent to{' '}
            <strong>Mapbox</strong> to get a real-time driving route including live traffic. The
            visual route line on the map is drawn using <strong>OSRM</strong> (OpenStreetMap). Both
            services receive only two coordinate pairs, nothing else.
          </li>
          <li>
            To route trip-day timings from your stay address. When a trip is active, the app uses
            your hotel address instead of home. For flights, trains, and buses it adds a terminal
            buffer (2 hours for airports, 1 hour for stations) on departure days.
          </li>
          <li>
            To send up to six push notification reminders before each show via OneSignal, based on
            your prep time, advance warning preference, and calculated drive time.
          </li>
          <li>
            To show a GPS-based banner on gig day if your device is more than 0.5 km from home,
            so you know how long it will take to get back. GPS is only used on gig day and only
            while the app is open. It is never tracked in the background.
          </li>
        </ul>
      </PolicySection>


      {/* 4. PUSH NOTIFICATIONS AND ONESIGNAL */}
      <PolicySection title="4. Push notifications and OneSignal">
        <p style={prose}>
          {APP_NAME} uses <strong>OneSignal</strong> to deliver push notifications. When you turn
          on notifications in Settings, OneSignal registers your device and gives it a subscription
          ID. We store that ID in your account so we can send you gig reminders.
        </p>
        <p style={{ ...prose, marginBottom: 'var(--spacing-sm)' }}>
          When you save a gig, the app schedules up to six notifications:
        </p>
        <ul style={listStyle}>
          <li><strong>Get Ready warning</strong> - fires your chosen advance time before your get-ready time. For example: "Get ready in 30 minutes."</li>
          <li><strong>Get Ready now</strong> - fires at your exact get-ready time. For example: "Time to get ready!"</li>
          <li><strong>Leave Home warning</strong> - fires if you need to leave home to reach a separate prep location (multi-stop routes only).</li>
          <li><strong>Leave Home now</strong> - fires at the exact time to leave home for your prep location (multi-stop routes only).</li>
          <li><strong>Leave warning</strong> - fires your advance time before your calculated leave time. For example: "Leave in 30 minutes."</li>
          <li><strong>Leave now</strong> - fires at your exact leave time. For example: "Time to leave!"</li>
        </ul>
        <p style={prose}>
          All notification times are calculated from your prep time, advance warning preference,
          and Mapbox drive time. They are stored as UTC timestamps and sent by a scheduled function
          that runs every minute. Re-saving a gig after changing the call time recalculates and
          resets all notifications automatically.
        </p>
        <p style={prose}>
          OneSignal may collect device and technical information as described in their privacy
          policy at{' '}
          <a href="https://onesignal.com/privacy_policy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-accessible)' }}>
            onesignal.com/privacy_policy
          </a>.
          We only pass OneSignal your subscription ID and the notification text. We never share your
          gig details, contact info, financial data, or trip data with OneSignal.
        </p>
        <p style={prose}>
          You can turn off notifications at any time in Settings. When you do, your subscription
          ID is removed from our database and your device is opted out. You can also manage
          notification permissions directly in your browser or device settings.
        </p>
        <p style={prose}>
          On iOS, push notifications require the app to be installed to your home screen (Share
          &gt; Add to Home Screen) using Safari 16.4 or later. On Android and desktop Chrome,
          they work directly from the browser.
        </p>
      </PolicySection>


      {/* 5. DATA STORAGE AND SECURITY */}
      <PolicySection title="5. Data storage and security">
        <p style={prose}>
          Your data is stored using <strong>Supabase</strong>, a cloud database platform hosted on
          AWS. Supabase enforces row-level security, which means only your account can access your
          data. All data is sent over encrypted HTTPS connections.
        </p>
        <p style={prose}>
          Your uploaded music tracks and promo photos are stored in Supabase Storage with access
          policies that lock them to your account only.
        </p>
        <p style={prose}>
          Your trip and travel data is stored in your account row under the same row-level security
          as everything else.
        </p>
        <p style={prose}>
          Terminal search results are cached in your browser's local storage only. They never go
          to our servers and are cleared when you clear your browser data.
        </p>
        <p style={prose}>
          We take reasonable security precautions, but no system is 100% secure. During beta,
          we recommend not putting highly sensitive information in the notes fields.
        </p>
        <p style={prose}>
          For more on Supabase's data practices, see the{' '}
          <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-accessible)' }}>
            Supabase Privacy Policy
          </a>.
        </p>
      </PolicySection>


      {/* 6. DATA RETENTION */}
      <PolicySection title="6. Data retention and deletion">
        <p style={prose}>
          We keep your data for as long as your account is active. When you delete your account,
          all data associated with it is permanently deleted immediately.
        </p>
        <p style={prose}>
          You can delete your account at the bottom of this page. You will be asked to type{' '}
          <strong>DELETE</strong> to confirm. Deletion is immediate and cannot be undone.
        </p>
        <p style={prose}>
          You can also delete individual gigs, contacts, music tracks, photos, and trip entries
          at any time from within the app. Deleted files are removed from storage immediately.
          Deleting a gig also removes all scheduled notifications for that gig.
        </p>
        <p style={prose}>
          <strong>Beta note:</strong> During beta, we may reset or migrate data as the app is
          updated. We will notify beta testers in advance of any data reset.
        </p>
      </PolicySection>


      {/* 7. THIRD-PARTY SERVICES */}
      <PolicySection title="7. Third-party services">
        <p style={{ ...prose, marginBottom: 'var(--spacing-sm)' }}>
          These are the third-party services the app uses. Each one receives only the minimum
          data it needs to do its job.
        </p>

        <div style={{ overflowX: 'auto', marginBottom: 'var(--spacing-md)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)', color: 'var(--neutral-1)' }}>
            <thead>
              <tr style={{ background: 'var(--pink-pale)' }}>
                {['Service', 'Purpose', 'Data Sent', 'Privacy Policy'].map(h => (
                  <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', borderBottom: '2px solid var(--accent-color)', fontWeight: 'var(--weight-black)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                {
                  name: 'Supabase',
                  purpose: 'Database, authentication, and file storage',
                  data: 'All account data, gigs, contacts, trips, files',
                  url: 'https://supabase.com/privacy',
                },
                {
                  name: 'Netlify',
                  purpose: 'Web hosting and deployment',
                  data: 'None beyond standard web requests',
                  url: 'https://www.netlify.com/privacy/',
                },
                {
                  name: 'OneSignal',
                  purpose: 'Push notification delivery for gig reminders',
                  data: 'Device subscription ID and notification text only',
                  url: 'https://onesignal.com/privacy_policy',
                },
                {
                  name: 'Mapbox',
                  purpose: 'Live driving directions and travel time calculations',
                  data: 'Origin and destination coordinates only',
                  url: 'https://www.mapbox.com/legal/privacy',
                },
                {
                  name: 'OSRM (OpenStreetMap)',
                  purpose: 'Visual driving route line on the gig detail map',
                  data: 'Origin and destination coordinates only',
                  url: 'https://osmfoundation.org/wiki/Privacy_Policy',
                },
                {
                  name: 'Nominatim (OpenStreetMap)',
                  purpose: 'Convert typed addresses to coordinates (home, venue, stay address, terminals)',
                  data: 'Address text or GPS coordinates you provide',
                  url: 'https://osmfoundation.org/wiki/Privacy_Policy',
                },
                {
                  name: 'Photon (Komoot)',
                  purpose: 'Venue name autocomplete in the gig form',
                  data: 'Venue name text as you type',
                  url: 'https://www.komoot.com/privacy',
                },
              ].map((row, i) => (
                <tr key={row.name} style={{ background: i % 2 === 0 ? 'var(--brand-white)' : 'transparent' }}>
                  <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--accent-color)', fontWeight: 'var(--weight-bold)', whiteSpace: 'nowrap' }}>{row.name}</td>
                  <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--accent-color)' }}>{row.purpose}</td>
                  <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--accent-color)', fontSize: 'var(--text-xs)' }}>{row.data}</td>
                  <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--accent-color)', whiteSpace: 'nowrap' }}>
                    <a href={row.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-accessible)' }}>View <i className="bi bi-box-arrow-up-right"></i></a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p style={prose}>
          We do not use Google Analytics, Facebook Pixel, or any advertising or behavioral
          tracking services.
        </p>
      </PolicySection>


      {/* 8. YOUR RIGHTS */}
      <PolicySection title="8. Your rights">
        <p style={{ ...prose, marginBottom: 'var(--spacing-sm)' }}>
          Regardless of where you are located, you can:
        </p>
        <ul style={listStyle}>
          <li><strong>See your data</strong> - everything you have entered is visible in the app at any time.</li>
          <li><strong>Edit your data</strong> - update or correct anything at any time within the app.</li>
          <li><strong>Delete your data</strong> - delete your account and everything in it (see Section 6).</li>
          <li><strong>Export your data</strong> - you can export gig data from within the app. A full JSON export is available on request via {CONTACT_LABEL}.</li>
          <li><strong>Turn off notifications</strong> - disable push notifications at any time in Settings.</li>
          <li><strong>Remove your location</strong> - delete your saved home address and coordinates at any time in Settings.</li>
          <li><strong>Delete trips</strong> - remove individual trips at any time from the Trip Planner.</li>
        </ul>
        <p style={prose}>
          EU and UK residents have additional rights under GDPR, including the right to complain
          to your local supervisory authority. California residents have additional rights
          described in Section 9 below.
        </p>
      </PolicySection>


      {/* 9. CALIFORNIA PRIVACY RIGHTS */}
      <PolicySection title="9. California privacy rights (CCPA / CPRA)">

        <p style={prose}>
          If you are a California resident, the CCPA (as amended by the CPRA) gives you specific
          rights. This section explains those rights in plain language.
        </p>

        <p style={{ ...prose, fontWeight: 'var(--weight-bold)', marginBottom: '0.5rem' }}>
          Categories of personal information we collect:
        </p>
        <ul style={listStyle}>
          <li><strong>Identifiers</strong> - email address, performer name, and IP address (logged by Supabase infrastructure).</li>
          <li><strong>Commercial information</strong> - gig payment amounts, earnings history, and tips you record.</li>
          <li><strong>Geolocation data</strong> - home address and coordinates if you enable reminders; stay addresses and terminal coordinates if you use the Trip Planner.</li>
          <li><strong>Audio and electronic data</strong> - music tracks you upload.</li>
          <li><strong>Images</strong> - promo photos you upload.</li>
          <li><strong>Internet or network activity</strong> - connection metadata logged by Supabase and OneSignal device identifiers for push notifications.</li>
          <li><strong>Professional information</strong> - gig details, venue contacts, booking information, travel and tour itineraries.</li>
          <li><strong>Inferences</strong> - none. We do not profile you or draw inferences for advertising.</li>
        </ul>

        <p style={{ ...prose, fontWeight: 'var(--weight-bold)', marginBottom: '0.5rem' }}>
          Do we sell or share your information?
        </p>
        <p style={prose}>
          No. We do <strong>not</strong> sell your personal information. We do <strong>not</strong> share
          it with third parties for cross-context behavioral advertising. If this ever changes,
          we will update this policy and give you a clear opt-out before it happens.
        </p>

        <p style={{ ...prose, fontWeight: 'var(--weight-bold)', marginBottom: '0.5rem' }}>
          Your CCPA / CPRA rights:
        </p>
        <ul style={listStyle}>
          <li>
            <strong>Right to Know</strong> - you can ask us what personal information we have
            collected about you, where it came from, why we use it, and who we share it with.
          </li>
          <li>
            <strong>Right to Delete</strong> - you can ask us to delete your personal information.
            We will delete it and direct our service providers to do the same.
          </li>
          <li>
            <strong>Right to Correct</strong> - you can ask us to fix inaccurate personal
            information we hold about you.
          </li>
          <li>
            <strong>Right to Opt Out of Sale or Sharing</strong> - we do not sell or share
            personal information as defined by the CCPA. No action is needed, but you can contact
            us to confirm.
          </li>
          <li>
            <strong>Right to Limit Use of Sensitive Personal Information</strong> - we only use
            sensitive personal information (geolocation, audio, travel itineraries) to provide
            the services you have asked for. We do not use it for anything else.
          </li>
          <li>
            <strong>Right to Non-Discrimination</strong> - exercising any of these rights will not
            result in denial of service, different pricing, or lower quality of service.
          </li>
        </ul>

        <p style={{ ...prose, fontWeight: 'var(--weight-bold)', marginBottom: '0.5rem' }}>
          How to make a request:
        </p>
        <p style={prose}>
          Contact us at{' '}
          <a href={CONTACT_SITE} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-accessible)' }}>
            {CONTACT_LABEL}
          </a>.
          Include your account email address so we can verify your identity. We will respond
          within <strong>45 calendar days</strong>. If we need more time (up to 90 days total),
          we will let you know in writing.
        </p>
        <p style={prose}>
          You may also designate an authorized agent to make a request on your behalf. We may
          still contact you directly to verify your identity before acting on the request.
        </p>
        <p style={prose}>
          We do not charge a fee to process requests.
        </p>

        <p style={{ ...prose, fontWeight: 'var(--weight-bold)', marginBottom: '0.5rem' }}>
          Shine the Light (California Civil Code 1798.83):
        </p>
        <p style={prose}>
          We do not disclose personal information to third parties for their direct marketing
          purposes, so there is nothing to report under this provision.
        </p>
      </PolicySection>


      {/* 10. CHILDREN'S PRIVACY */}
      <PolicySection title="10. Children's privacy">
        <p style={prose}>
          {APP_NAME} is for adults (18 and older). We do not knowingly collect personal
          information from anyone under 18. If you believe a child under 16 has created an account,
          please contact us at{' '}
          <a href={CONTACT_SITE} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-accessible)' }}>{CONTACT_LABEL}</a>{' '}
          and we will delete the account and all associated data immediately.
        </p>
      </PolicySection>


      {/* 11. CHANGES TO THIS POLICY */}
      <PolicySection title="11. Changes to this policy">
        <p style={prose}>
          We may update this policy as the app evolves. When we make significant changes, we
          will update the effective date at the top of this page. For changes that affect
          California residents' rights, we will give at least 30 days' notice before they
          take effect.
        </p>
        <p style={prose}>
          Continued use of the app after changes are posted means you accept the updated policy.
        </p>
      </PolicySection>


      {/* 12. CONTACT */}
      <PolicySection title="12. Contact us">
        <p style={prose}>
          Questions about this policy, your data, or your California privacy rights? Get in touch:
        </p>
        <div style={{
          background: 'var(--pink-pale)',
          border: '2px solid var(--primary-light)',
          borderRadius: 'var(--radius-card)',
          padding: 'var(--spacing-md)',
          display: 'inline-block',
        }}>
          <p style={{ margin: '0 0 0.5rem', fontWeight: 'var(--weight-black)', color: 'var(--neutral-1)' }}>
            {APP_NAME}
          </p>
          <p style={{ margin: 0 }}>
            <a href={CONTACT_SITE} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-accessible)', fontWeight: 'var(--weight-bold)' }}>
              <i className="bi bi-envelope-fill"></i> {CONTACT_LABEL}
            </a>
          </p>
          <p style={{ margin: '0.4rem 0 0', fontSize: 'var(--text-xs)', color: 'var(--neutral-1)', opacity: 0.65 }}>
            For CCPA requests, include your account email. We respond within 45 days.
          </p>
        </div>
      </PolicySection>


      {/* DELETE ACCOUNT
          Placed at the bottom of the privacy policy so it is harder to
          reach by accident. The three-step confirmation gate makes it
          impossible to delete an account unintentionally. */}
      <div style={{
        marginTop: 'var(--spacing-xl)',
        marginBottom: 'var(--spacing-xl)',
        border: '2px solid var(--tertiary-3, #e53935)',
        borderRadius: 'var(--radius-card)',
        overflow: 'hidden',
      }}>

        {/* Danger zone header */}
        <div style={{
          background: 'var(--tertiary-3, #e53935)',
          padding: 'var(--spacing-sm) var(--spacing-md)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
        }}>
          <i className="bi bi-exclamation-triangle-fill" style={{ color: '#fff', fontSize: '1rem' }}></i>
          <span style={{ color: '#fff', fontWeight: 'var(--weight-black)', fontSize: 'var(--text-sm)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Danger Zone
          </span>
        </div>

        <div style={{ padding: 'var(--spacing-md)' }}>

          {/* Collapsed state */}
          {!showDeleteConfirm && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--spacing-sm)' }}>
              <div>
                <p style={{ margin: '0 0 0.25rem', fontWeight: 'var(--weight-bold)', color: 'var(--neutral-1)' }}>
                  Delete My Account
                </p>
                <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--neutral-1)', opacity: 0.65 }}>
                  Permanently deletes your account and all associated data. This cannot be undone. Make sure you expoerted whateever data you may want to keep a record of.
                </p>
              </div>
              <button
                className="btn-danger"
                onClick={() => setShowDeleteConfirm(true)}
                style={{ flexShrink: 0 }}
              >
                <i className="bi bi-trash3-fill" style={{ marginRight: '0.4rem' }}></i>
                Delete My Account
              </button>
            </div>
          )}

          {/* Expanded confirmation state */}
          {showDeleteConfirm && (
            <div>
              <p style={{ fontWeight: 'var(--weight-bold)', color: 'var(--neutral-1)', marginBottom: 'var(--spacing-sm)' }}>
                <i className="bi bi-exclamation-circle-fill" style={{ color: 'var(--tertiary-3, #e53935)', marginRight: '0.4rem' }}></i>
                The following will be permanently deleted and cannot be recovered:
              </p>
              <ul style={{ ...listStyle, marginBottom: 'var(--spacing-md)' }}>
                <li>Your account and login credentials</li>
                <li>All gigs, earnings, and setlists</li>
                <li>All contacts and booking information</li>
                <li>All uploaded music tracks and promo photos</li>
                <li>Your notification preferences and home location</li>
                <li>All trips and travel itineraries</li>
                <li>All scheduled notifications</li>
                <li>Your travel checklist</li>
                <li>Your performer name, social handles, and profile settings</li>
              </ul>

              <p style={{ fontWeight: 'var(--weight-bold)', color: 'var(--neutral-1)', marginBottom: '0.5rem' }}>
                Type <strong style={{ color: 'var(--tertiary-3, #e53935)', fontFamily: 'monospace', fontSize: '1.05em' }}>DELETE</strong> to confirm:
              </p>
              <input
                type="text"
                placeholder="Type DELETE here"
                value={deleteInput}
                onChange={e => setDeleteInput(e.target.value)}
                disabled={deleting}
                autoComplete="off"
                style={{
                  width: '100%',
                  maxWidth: '320px',
                  padding: '0.5rem 0.75rem',
                  border: `2px solid ${canDelete ? 'var(--tertiary-3, #e53935)' : 'var(--accent-color)'}`,
                  borderRadius: 'var(--radius-sm)',
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-base)',
                  color: 'var(--neutral-1)',
                  marginBottom: 'var(--spacing-md)',
                  display: 'block',
                }}
              />

              {deleteError && (
                <div style={{
                  background: 'rgba(229,57,53,0.08)',
                  border: '1px solid var(--tertiary-3, #e53935)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '0.6rem 0.85rem',
                  marginBottom: 'var(--spacing-md)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--tertiary-3, #e53935)',
                }}>
                  <i className="bi bi-x-circle-fill" style={{ marginRight: '0.4rem' }}></i>
                  {deleteError}
                </div>
              )}

              <div style={{ display: 'flex', gap: 'var(--spacing-sm)', flexWrap: 'wrap' }}>
                <button
                  className="btn-danger"
                  onClick={handleDeleteAccount}
                  disabled={!canDelete || deleting}
                  style={{ opacity: (!canDelete || deleting) ? 0.5 : 1 }}
                >
                  {deleting
                    ? <><span className="spinner-border spinner-border-sm me-1" style={{ width: '0.85rem', height: '0.85rem', borderWidth: '0.15em' }} /> Deleting...</>
                    : <><i className="bi bi-trash3-fill" style={{ marginRight: '0.4rem' }}></i>Permanently Delete My Account</>
                  }
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => { setShowDeleteConfirm(false); setDeleteInput(''); setDeleteError(null) }}
                  disabled={deleting}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

        </div>
      </div>


      {/* Footer */}
      <div style={{
        borderTop: '2px solid var(--accent-color)',
        paddingTop: 'var(--spacing-lg)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 'var(--spacing-sm)',
      }}>
        <span style={{ color: 'var(--neutral-1)', opacity: 0.5, fontSize: 'var(--text-sm)' }}>
          &copy; {new Date().getFullYear()} {APP_NAME} &middot; Effective {EFFECTIVE_DATE}
        </span>
        <Link to="/about" style={{ color: 'var(--primary-accessible)', fontSize: 'var(--text-sm)', fontWeight: 'var(--weight-bold)' }}>
          <i className="bi bi-arrow-left"></i> Back to About
        </Link>
      </div>

    </div>
  )
}

export default PrivacyPolicy